import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/reducers/rootReducer";
import { MeAssignmentDataScope, CmLocation } from "../redux/types/permissionTypes";
import { SYSTEM } from "../containers/PermissionManagement/constants";
import { SystemType } from "../config/routeConfig";

/** Dimension attributes of a data record, used to match against me assignment data_scope. */
export interface DataRecord {
  cm?: string;
  location?: string;
  program?: string;
  operation?: string;
}

/**
 * Match a single data line against an assignment's data_scope.
 * Rules:
 *  - "All" is a wildcard that matches any value.
 *  - Omitted fields are not constrained (treated as match).
 *  - cm and location must match within the same cm_locations entry.
 */
function isDataLineMatch(scope: MeAssignmentDataScope, record: DataRecord): boolean {
  if (record.operation) {
    if (!scope.operations.includes("All") && !scope.operations.includes(record.operation)) {
      return false;
    }
  }

  // cm and location must satisfy the same cm_locations entry
  if (record.cm || record.location) {
    const cmMatch = scope.cm_locations.some(
      (cl) =>
        (!record.cm || cl.cm === "All" || cl.cm === record.cm) &&
        (!record.location || cl.location.includes("All") || cl.location.includes(record.location))
    );
    if (!cmMatch) return false;
  }

  if (record.program) {
    if (!scope.programs.includes("All") && !scope.programs.includes(record.program)) {
      return false;
    }
  }

  return true;
}

/** Full datascope shape representing multiple operations, cm_locations, and programs. */
export interface FullDataScope {
  operations: string[];
  cm_locations: CmLocation[];
  programs: string[];
}

/**
 * Match multiple data lines (full datascope) against a set of user assignment scopes.
 * "All" in the item requires "All" in at least one user scope for that dimension.
 * Specific values use the single data line matching logic against the union of user scopes.
 */
function isDataLinesMatch(userScopes: MeAssignmentDataScope[], itemScope: FullDataScope): boolean {
  for (const op of itemScope.operations) {
    if (op === "All") {
      if (!userScopes.some((s) => s.operations.includes("All"))) return false;
    } else {
      if (!userScopes.some((s) => isDataLineMatch(s, { operation: op }))) return false;
    }
  }

  for (const cl of itemScope.cm_locations) {
    if (cl.cm === "All") {
      if (!userScopes.some((s) => s.cm_locations.some((c) => c.cm === "All"))) return false;
    } else {
      for (const loc of cl.location) {
        if (loc === "All") {
          if (!userScopes.some((s) => s.cm_locations.some((c) => c.cm === "All" || (c.cm === cl.cm && c.location.includes("All"))))) return false;
        } else {
          if (!userScopes.some((s) => isDataLineMatch(s, { cm: cl.cm, location: loc }))) return false;
        }
      }
    }
  }

  for (const prog of itemScope.programs) {
    if (prog === "All") {
      if (!userScopes.some((s) => s.programs.includes("All"))) return false;
    } else {
      if (!userScopes.some((s) => isDataLineMatch(s, { program: prog }))) return false;
    }
  }

  return true;
}

/**
 * Unified permission hook providing two layers of access control:
 *
 * 1. hasPermission(key) — Feature-level (menu/button visibility).
 *    Merges permissions across all assignments regardless of data_scope.
 *    Key format: "feature.action", e.g. "configuration_setting.cs_view_all"
 *
 * 2. hasDataPermission(feature, action, record) — Data-level (per-record action control).
 *    Matches the record's cm/location/program/operation against each assignment's data_scope,
 *    then merges permissions only from matched assignments. Multiple matches are unioned.
 *
 * Usage:
 *   const { hasPermission, hasDataPermission } = usePermission();
 *   hasPermission("configuration_setting.cs_view_all")
 *   hasDataPermission("configuration_setting", "cs_update", { cm: "CNT", location: "Shenzhen" })
 */
export function usePermission() {

  const meData = useSelector((state: RootState) => state.permission.me.data);

  const isSystemAdmin = useMemo(() => {
    if (!meData) return false;
    return !!meData.is_system_admin?.[SYSTEM];
  }, [meData]);

  const isFeatureFullAccess = useMemo(() => {
    if (!meData) return false;
    if (isSystemAdmin) return true;
    return meData.assignments
      ?.filter((a) => a.system === SYSTEM)
      .some((a) => a.permissions.some((p) => p.feature === "All" && p.actions.includes("All"))) ?? false;
  }, [meData, isSystemAdmin]);

  // Merge all role permissions into { feature: Set<action> }
  const permissionMap = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    if (!meData || isFeatureFullAccess) return result;

    meData.assignments
      ?.filter((a) => a.system === SYSTEM)
      .forEach((a) => {
        a.permissions.forEach(({ feature, actions }) => {
          if (!result[feature]) {
            result[feature] = new Set();
          }
          actions.forEach((action) => result[feature]!.add(action));
        });
      });

    return result;
  }, [meData, isFeatureFullAccess]);

  // Feature-level (menu/button visibility)
  const hasPermission = useCallback((key: string, system?: SystemType): boolean => {
    if (!meData) return false;
    if (isFeatureFullAccess && (!system || system === "global" || system === SYSTEM)) return true;

    const [feature, action] = key.split(".");
    if (!feature || !action) return false;

    const allowedActions = permissionMap[feature];
    if (!allowedActions) return false;

    return allowedActions.has(action);
  }, [meData, isFeatureFullAccess, permissionMap]);

  const getDataActions = useCallback(
    (feature: string, record: DataRecord): Set<string> => {
      if (!meData) return new Set();
      if (isSystemAdmin) return new Set(["All"]);

      const actions = new Set<string>();

      meData.assignments
        ?.filter((a) => a.system === SYSTEM)
        .filter((a) => isDataLineMatch(a.data_scope, record))
        .forEach((a) => {
          a.permissions.forEach((p) => {
            if (p.feature === "All" || p.feature === feature) {
              p.actions.forEach((action) => actions.add(action));
            }
          });
        });

      return actions;
    },
    [meData, isSystemAdmin]
  );
  // Data-level (per-record action control)
  const hasDataPermission = useCallback(
    (feature: string, action: string, record: DataRecord): boolean => {
      if (!meData) return false;
      if (isSystemAdmin) return true;
      const allowed = getDataActions(feature, record);
      return allowed.has("All") || allowed.has(action);
    },
    [meData, isSystemAdmin, getDataActions]
  );

  // Full datascope coverage check: whether the current user's scopes cover an entire multi-dimensional datascope
  const hasDataScopeCoverage = useCallback(
    (feature: string, action: string, itemScope: FullDataScope): boolean => {
      if (!meData) return false;
      if (isSystemAdmin) return true;

      const matchedScopes = meData.assignments
        ?.filter((a) => a.system === SYSTEM)
        .filter((a) => a.permissions.some(
          (p) => (p.feature === "All" || p.feature === feature) && (p.actions.includes("All") || p.actions.includes(action))
        ))
        .map((a) => a.data_scope) ?? [];

      return isDataLinesMatch(matchedScopes, itemScope);
    },
    [meData, isSystemAdmin]
  );

  return { hasPermission, hasDataPermission, hasDataScopeCoverage };
}
